import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { RouterModule } from "@angular/router";

import { TranslateModule } from "@ngx-translate/core";

import {
  MatMomentDateModule,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from "@angular/material-moment-adapter";
import { MatLegacyAutocompleteModule } from "@angular/material/legacy-autocomplete";
import { MatLegacyCheckboxModule } from "@angular/material/legacy-checkbox";
import { DateAdapter } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatLegacyDialogModule } from "@angular/material/legacy-dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatLegacyRadioModule } from "@angular/material/legacy-radio";
import { MatLegacySelectModule } from "@angular/material/legacy-select";
import { MatLegacySlideToggleModule } from "@angular/material/legacy-slide-toggle";
import { MatLegacyTooltipModule } from "@angular/material/legacy-tooltip";
import {
  NgbCollapseModule,
  NgbTooltipModule,
} from "@ng-bootstrap/ng-bootstrap";
import { InfiniteScrollModule } from "ngx-infinite-scroll";

import { DecimalPipe } from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { JssModule } from "@sitecore-jss/sitecore-jss-angular";
import { NgxMaskModule } from "ngx-mask";
import { GoogleAnalyticsModule } from "../google-analytics/google-analytics.module";
import { CountryListComponent } from "../shared/components/country-list/country-list.component";
import { ValidatorsModule } from "../validators/validators.module";
import { AbDateAdapter } from "./adapters/ab-date-adapter";
import { AccessDeniedComponent } from "./components/access-denied/access-denied.component";
import { AddressBookComponent } from "./components/address-book/address-book.component";
import { AddressComponent } from "./components/address/address.component";
import { BankListComponent } from "./components/bank-list/bank-list.component";
import { CurrencyInputComponent } from "./components/currency-input/currency-input.component";
import { FileUploadFieldComponent } from "./components/file-upload-field/file-upload-field.component";
import { ForeignBankComponent } from "./components/foreign-bank/foreign-bank.component";
import { LoaderComponent } from "./components/loader/loader.component";
import { PrintComponent } from "./components/print/print.component";
import { RichtextComponent } from "./components/richtext/richtext.component";
import { TabNavigationComponent } from "./components/tab-navigation/tab-navigation.component";
import { TableComponent } from "./components/table/table.component";
import { AccessDirective } from "./directives/access.directive";
import { AmountFormatterDirective } from "./directives/amount-formatter.directive";
import { ButtonLoaderDirective } from "./directives/button-loader.directive";
import { DateFormatterDirective } from "./directives/date-formatter.directive";
import { FileDropZoneDirective } from "./directives/file-drop-zone.directive";
import { ScrollInvalidDirective } from "./directives/scroll-invalid.directive";
import { ScrollToDirective } from "./directives/scroll-to.directive";
import { UnitsDirective } from "./directives/units.directive";
import { AbCurrencySymbolPipe } from "./pipes/ab-currency-symbol.pipe";
import { AbCurrencyPipe } from "./pipes/ab-currency.pipe";
import { AbDatePipe } from "./pipes/ab-date.pipe";
import { AbFileSizePipe } from "./pipes/ab-file-size.pipe";
import { AbFilterCountriesPipe } from "./pipes/ab-filter-countries.pipe";
import { AbMoneyPipe } from "./pipes/ab-money.pipe";
import { ReplacePipe } from "./pipes/replace.pipe";
import { SafeHtmlPipe } from "./pipes/safe-html.pipe";
import { SupFractionPipe } from "./pipes/sup-fraction.pipe";
import { AntiForgeryTokenService } from "./services/anti-forgery-token.service";
import { ButtonLoaderInterceptorService } from "./services/button-loader-interceptor.service";
import { ErrorHandlingInterceptorService } from "./services/error-handling-interceptor.service";
import { HttpCancelInterceptorService } from "./services/http-cancel-interceptor.service";
import { LoaderInterceptorService } from "./services/loader-interceptor.service";
import { IFrameResizerDirective } from "./directives/iframeResizer.directive";

const SharedComponents = [
  { name: "RichText", type: RichtextComponent },
  { name: "TabsMenu", type: TabNavigationComponent },
  { name: "AccessDenied", type: AccessDeniedComponent },
  { name: "Print", type: PrintComponent },
];

@NgModule({
  imports: [
    CommonModule,
    JssModule,
    TranslateModule,
    MatLegacyDialogModule,
    MatLegacySlideToggleModule,
    MatLegacyCheckboxModule,
    MatLegacyRadioModule,
    RouterModule,
    MatLegacyTooltipModule,
    MatExpansionModule,
    MatLegacySelectModule,
    MatDatepickerModule,
    MatMomentDateModule,
    NgbCollapseModule,
    NgbTooltipModule,
    InfiniteScrollModule,
    ValidatorsModule,
    MatLegacyAutocompleteModule,
    GoogleAnalyticsModule,
    NgxMaskModule.forRoot(),
  ],
  declarations: [
    LoaderComponent,
    RichtextComponent,
    TableComponent,
    TabNavigationComponent,
    AbDatePipe,
    AmountFormatterDirective,
    DateFormatterDirective,
    CurrencyInputComponent,
    AbCurrencySymbolPipe,
    AbMoneyPipe,
    AbCurrencyPipe,
    AbFilterCountriesPipe,
    BankListComponent,
    AddressComponent,
    CountryListComponent,
    ForeignBankComponent,
    AccessDeniedComponent,
    AddressBookComponent,
    SafeHtmlPipe,
    FileDropZoneDirective,
    AbFileSizePipe,
    FileUploadFieldComponent,
    PrintComponent,
    UnitsDirective,
    ButtonLoaderDirective,
    ScrollInvalidDirective,
    SupFractionPipe,
    AccessDirective,
    IFrameResizerDirective,
    ScrollToDirective,
    ReplacePipe,
  ],
  exports: [
    LoaderComponent,
    RichtextComponent,
    MatLegacySlideToggleModule,
    TabNavigationComponent,
    NgbCollapseModule,
    NgbTooltipModule,
    MatLegacyCheckboxModule,
    MatLegacyRadioModule,
    RouterModule,
    AbDatePipe,
    AbMoneyPipe,
    AbCurrencyPipe,
    AbCurrencySymbolPipe,
    MatExpansionModule,
    AmountFormatterDirective,
    MatLegacyTooltipModule,
    MatLegacySelectModule,
    MatDatepickerModule,
    MatMomentDateModule,
    DateFormatterDirective,
    CurrencyInputComponent,
    InfiniteScrollModule,
    ValidatorsModule,
    MatLegacyDialogModule,
    MatLegacyAutocompleteModule,
    AbFilterCountriesPipe,
    BankListComponent,
    AddressComponent,
    CountryListComponent,
    ForeignBankComponent,
    AccessDeniedComponent,
    AddressBookComponent,
    SafeHtmlPipe,
    FileDropZoneDirective,
    AbFileSizePipe,
    FileUploadFieldComponent,
    PrintComponent,
    UnitsDirective,
    ButtonLoaderDirective,
    ScrollInvalidDirective,
    GoogleAnalyticsModule,
    SupFractionPipe,
    ScrollToDirective,
    ReplacePipe,
  ],
  providers: [
    DecimalPipe,
    AbMoneyPipe,
    AbDatePipe,
    AbCurrencyPipe,
    SafeHtmlPipe,
    SupFractionPipe,
    { provide: DateAdapter, useClass: AbDateAdapter },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCancelInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlingInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ButtonLoaderInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AntiForgeryTokenService,
      multi: true,
    },
  ],
})
export class SharedModule {}
export { SharedComponents };
